import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import LegalModal from "../components/LegalModal";
import ColdStartLoader from "../components/ColdStartLoader";
import { Eye, EyeOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

export default function Register() {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    full_name: ""
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [pmAndDataConfirmed, setPmAndDataConfirmed] = useState(false);
  const [termsUsageAccepted, setTermsUsageAccepted] = useState(false);
  const [allAccepted, setAllAccepted] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [legalModalType, setLegalModalType] = useState(null);
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [showEmailSuccessModal, setShowEmailSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const openLegalModal = (type) => {
    setLegalModalType(type);
    setShowLegalModal(true);
  };

  const handleAllAcceptedChange = (checked) => {
    setAllAccepted(checked);
    setTermsAccepted(checked);
    setPrivacyAccepted(checked);
    setPmAndDataConfirmed(checked);
    setTermsUsageAccepted(checked);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwörter stimmen nicht überein");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen lang sein");
      return;
    }

    if (!termsAccepted) {
      toast.error("Bitte akzeptieren Sie den Haftungsausschluss");
      return;
    }

    if (!privacyAccepted) {
      toast.error("Bitte akzeptieren Sie die Datenschutzerklärung");
      return;
    }

    if (!pmAndDataConfirmed) {
      toast.error("Bitte bestätigen Sie, dass Sie PM-International Teampartner sind und zur Dateneingabe berechtigt sind");
      return;
    }

    if (!termsUsageAccepted) {
      toast.error("Bitte akzeptieren Sie die Nutzungsbedingungen");
      return;
    }

    // Show Beta Modal before actual registration
    setShowBetaModal(true);
  };

  const handleBetaConfirm = async () => {
    setShowBetaModal(false);
    setLoading(true);

    try {
      await axios.post(`${API}/api/auth/register`, {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        terms_accepted: termsAccepted,
        privacy_accepted: privacyAccepted,
        pm_teampartner_confirmed: pmAndDataConfirmed,
        third_party_data_confirmed: pmAndDataConfirmed,
        terms_usage_accepted: termsUsageAccepted
      });

      toast.success("Registrierung erfolgreich! Bitte bestätige deine Email-Adresse.", {
        description: "Wir haben dir eine Email gesendet. Prüfe auch deinen Spam-Ordner.",
        duration: 6000
      });
      // Zeige Info-Modal für Email-Bestätigung
      setShowEmailSuccessModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registrierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Cold Start Loader Overlay - rendered outside Card for full screen */}
      {loading && <ColdStartLoader message="Registrierung läuft..." />}
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center px-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {/* Logo */}
          <div className="flex justify-center mb-4">
            <img 
              src="/burningteam-logo.png" 
              alt="BurningTeam Logo" 
              className="h-20 w-auto"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-slate-800">
            Registrieren
          </CardTitle>
          <p className="text-slate-500 mt-2">
            Erstellen Sie Ihr BurningTeam-Konto
          </p>
          {/* Beta Badge */}
          <div className="mt-3">
            <span className="inline-block px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
              Beta Version
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Benutzername *</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                required
                placeholder="Wählen Sie einen Benutzernamen"
              />
            </div>
            
            <div>
              <Label htmlFor="email">E-Mail Adresse *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                placeholder="ihre.email@beispiel.com"
              />
            </div>
            
            <div>
              <Label htmlFor="full_name">Vollständiger Name</Label>
              <Input
                id="full_name"
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                placeholder="Max Mustermann (optional)"
              />
            </div>

            <div>
              <Label htmlFor="password">Passwort *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  placeholder="Mindestens 6 Zeichen"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Passwort bestätigen *</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  required
                  placeholder="Passwort wiederholen"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Legal Consents */}
            <div className="space-y-3 pt-4 border-t border-slate-200">
              {/* Master Checkbox - Alle bestätigen */}
              <div className="flex items-start space-x-2 pb-3 border-b border-slate-200">
                <input
                  type="checkbox"
                  id="allAccepted"
                  checked={allAccepted}
                  onChange={(e) => handleAllAcceptedChange(e.target.checked)}
                  className="mt-1 h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <label htmlFor="allAccepted" className="text-sm font-semibold text-slate-900 leading-tight cursor-pointer">
                  Alle Bestimmungen bestätigen
                </label>
              </div>

              {/* 1. Haftungsausschluss */}
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  required
                />
                <label htmlFor="terms" className="text-sm text-slate-700 leading-tight">
                  Ich habe den{" "}
                  <button
                    type="button"
                    onClick={() => openLegalModal('terms')}
                    className="text-emerald-600 hover:text-emerald-700 font-semibold underline"
                  >
                    Haftungsausschluss
                  </button>
                  {" "}gelesen und akzeptiere, dass die App keine Gewähr für die Richtigkeit, Vollständigkeit oder Aktualität der bereitgestellten Informationen übernimmt. Die Inhalte ersetzen keine rechtliche oder steuerliche Beratung. Ich verpflichte mich, alle Angaben eigenständig zu prüfen. *
                </label>
              </div>

              {/* 2. Datenschutzerklärung */}
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="privacy"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  required
                />
                <label htmlFor="privacy" className="text-sm text-slate-700 leading-tight">
                  Mit der Registrierung erkläre ich mich damit einverstanden, dass meine personenbezogenen Daten (z. B. Name, E-Mail-Adresse) zum Zweck der Nutzung dieser App verarbeitet werden. Ich habe die{" "}
                  <button
                    type="button"
                    onClick={() => openLegalModal('privacy')}
                    className="text-emerald-600 hover:text-emerald-700 font-semibold underline"
                  >
                    Datenschutzerklärung
                  </button>
                  {" "}gelesen und akzeptiere sie. *
                </label>
              </div>

              {/* 3. PM-International Teampartner & Datenberechtigung (kombiniert) */}
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="pmAndData"
                  checked={pmAndDataConfirmed}
                  onChange={(e) => setPmAndDataConfirmed(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  required
                />
                <label htmlFor="pmAndData" className="text-sm text-slate-700 leading-tight">
                  Ich bestätige, dass ich PM-International Teampartner bin und die App ausschließlich zu internen Organisationszwecken nutze. Ich versichere, dass ich zur Eingabe, Verarbeitung und Speicherung von Daten Dritter (z. B. Kunden oder Teampartnern) berechtigt bin und alle datenschutzrechtlichen Pflichten gegenüber diesen Personen einhalte. *
                </label>
              </div>

              {/* 4. Nutzungsbedingungen */}
              <div className="flex items-start space-x-2">
                <input
                  type="checkbox"
                  id="termsUsage"
                  checked={termsUsageAccepted}
                  onChange={(e) => setTermsUsageAccepted(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                  required
                />
                <label htmlFor="termsUsage" className="text-sm text-slate-700 leading-tight">
                  Ich habe die{" "}
                  <button
                    type="button"
                    onClick={() => openLegalModal('nutzungsbedingungen')}
                    className="text-emerald-600 hover:text-emerald-700 font-semibold underline"
                  >
                    Nutzungsbedingungen
                  </button>
                  {" "}gelesen und akzeptiere sie. Mir ist bewusst, dass ich die App auf eigenes Risiko nutze und keine Garantie für ständige Verfügbarkeit oder Fehlerfreiheit besteht. *
                </label>
              </div>
            </div>

            {/* Disclaimer Text */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs text-amber-800 leading-relaxed">
                Diese App ist ein privates Organisationstool. Alle Angaben und Berechnungen erfolgen ohne Gewähr. Nutzer sind verpflichtet, alle Daten selbst zu prüfen.
              </p>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
              disabled={loading || !termsAccepted || !privacyAccepted || !pmAndDataConfirmed || !termsUsageAccepted}
            >
              Konto erstellen
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600">
              Bereits ein Konto? {" "}
              <Link 
                to="/login" 
                className="text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Hier anmelden
              </Link>
            </p>
          </div>

          {/* PayPal Donation Button */}
          <div className="mt-8 pt-6 border-t border-slate-200 text-center">
            <p className="text-sm text-slate-500 mb-3">
              Gefällt Ihnen diese App? Unterstützen Sie die Entwicklung!
            </p>
            <a 
              href="https://www.paypal.com/donate/?hosted_button_id=WPK5YE395ZHUJ"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-[#0070ba] hover:bg-[#005ea6] text-white rounded-lg transition-colors duration-200 text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a7.7 7.7 0 0 1-.32 1.433c-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9L7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81.298.34.563.729.774 1.107z"/>
              </svg>
              Spenden via PayPal
            </a>
          </div>
        </CardContent>
      </Card>

      <LegalModal 
        isOpen={showLegalModal}
        onClose={() => setShowLegalModal(false)}
        type={legalModalType}
      />

      {/* Beta Warning Modal */}
      {showBetaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-amber-100 rounded-full p-3">
                  <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-bold text-slate-900 text-center">⚠️ Beta Version</h2>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4 text-sm text-slate-700">
                <p className="font-semibold text-center text-amber-600">
                  Diese App befindet sich in der Beta-Phase!
                </p>
                <p>
                  Bitte beachten Sie, dass diese Anwendung sich noch in der Entwicklung befindet. Es können Fehler auftreten oder Funktionen noch nicht vollständig sein.
                </p>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm">
                    <strong>Wichtig:</strong><br/>
                    • Keine Garantie für Fehlerfreiheit<br/>
                    • Regelmäßige Updates und Verbesserungen<br/>
                    • Feedback ist willkommen
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-200 bg-slate-50 space-y-2">
              <button
                onClick={handleBetaConfirm}
                className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
              >
                Verstanden, fortfahren
              </button>
              <button
                onClick={() => setShowBetaModal(false)}
                className="w-full px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Verification Success Modal */}
      {showEmailSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            {/* Header */}
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-center mb-4">
                <div className="bg-emerald-100 rounded-full p-3">
                  <svg className="w-8 h-8 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-bold text-slate-900 text-center">📧 Bestätige deine Email</h2>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="space-y-4 text-sm text-slate-700">
                <p className="font-semibold text-center text-emerald-600">
                  Registrierung erfolgreich!
                </p>
                <p>
                  Wir haben dir eine <span className="font-semibold">Bestätigungs-Email</span> an <strong>{formData.email}</strong> gesendet.
                </p>
                <p>
                  Bitte klicke auf den Link in der Email, um deinen Account zu aktivieren.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm">
                    <strong>💡 Email nicht erhalten?</strong><br/>
                    • Prüfe deinen Spam-Ordner<br/>
                    • Warte ein paar Minuten<br/>
                    • Die Email kommt von: noreply@burningteam.de
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => {
                  setShowEmailSuccessModal(false);
                  navigate('/login');
                }}
                className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
              >
                Zum Login
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
}